import { useEffect } from 'react';
import { ImoduleFilter } from 'types/global';
import useFiltersState from 'state/useFiltersState';
import { ISyncFilterEntry } from './filter.constants';

interface IUseSyncAllFiltersFromFocusedValuesOptions {
  syncEntries: ISyncFilterEntry[];
  setOrdersFilters: (filters: ImoduleFilter[]) => void;
}

/**
 * Sincroniza todos los filtros en ordersFilters a partir de los valores enfocados.
 * Aplica en un solo efecto la lógica de sincronizar cada filtro (agregar/actualizar/eliminar según valor)
 * para todas las entradas, usando getState() de Zustand para evitar race conditions.
 *
 * @param options.syncEntries - Lista de { filterName, focusedValue, getFilterValue }
 * @param options.setOrdersFilters - Setter del estado de filtros
 */
const useSyncAllFiltersFromFocusedValues = ({
  syncEntries,
  setOrdersFilters
}: IUseSyncAllFiltersFromFocusedValuesOptions) => {
  useEffect(() => {
    const latestFilters = useFiltersState.getState().ordersFilters;
    let currentFilters = [...latestFilters];
    let hasChanges = false;

    for (let i = 0; i < syncEntries.length; i++) {
      const entry = syncEntries[i];
      const filterValue =
        entry.focusedValue === null ? null : entry.getFilterValue(entry.focusedValue);
      const filterIndex = currentFilters.findIndex(filter => filter.name === entry.filterName);

      if (filterValue !== null) {
        const valuesToSet = Array.isArray(filterValue) ? filterValue : [filterValue];
        if (filterIndex >= 0) {
          const existingValues = currentFilters[filterIndex].values;
          if (
            existingValues?.length === valuesToSet.length &&
            valuesToSet.every((v, idx) => existingValues[idx] === v)
          ) {
            continue;
          }
          currentFilters[filterIndex] = {
            ...currentFilters[filterIndex],
            values: valuesToSet
          };
        } else {
          currentFilters.push({
            name: entry.filterName,
            includes: 'in',
            values: valuesToSet
          });
        }
        hasChanges = true;
      } else if (filterIndex >= 0) {
        currentFilters = currentFilters.slice(0, filterIndex).concat(currentFilters.slice(filterIndex + 1));
        hasChanges = true;
      }
    }

    if (hasChanges) {
      setOrdersFilters(currentFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncEntries, setOrdersFilters]);
};

export default useSyncAllFiltersFromFocusedValues;
